<script lang="ts">
import { RatingTarget } from '@smartsupp/websocket-client-visitor'
import { scrollIntoView } from 'seamless-scroll-polyfill'
import { onDestroy, onMount, tick } from 'svelte'

import MessageGroup from '@/components/content/MessageGroup.svelte'
import TypingMessage from '@/components/content/TypingMessage.svelte'
import { scrollContentListCounter } from '@/modules/app'
import { addRatingMessage, deleteRatingMessage, isAgentTyping, suggestedRating } from '@/modules/chat'
import { RATE_MESSAGE_ID } from '@/modules/chatRating'
import { isMessengerFrameExpanded } from '@/modules/frames'
import { isReplyProcessing, lastMessageId, messageGroups } from '@/modules/messages'
import { messengerInputValue, textareaHeight } from '@/modules/textarea'
import { Timeout } from '@/utils/timeout'

import ContentListItem from './WidgetMessengerContentListItem.svelte'

let listRef: HTMLDivElement | undefined = $state()
let typingRef: HTMLDivElement | undefined = $state()
let groups = $derived($messageGroups)

const listItemsMap: Record<string, HTMLDivElement> = $state({})
const TIMEOUT = 5000
const ratingTimeout = new Timeout(() => {
	void addRatingMessage(RatingTarget.Ai)
}, TIMEOUT)

onMount(() => {
	$lastMessageId && scrollListItemIntoView($lastMessageId)
})

onDestroy(() => {
	ratingTimeout.stop()
})

const scrollToBottom = () => {
	if (!listRef) return
	listRef.scrollTop = listRef.scrollHeight
}

const lockScrollToBottom = (duration: number, durrationOffset = 0) => {
	const startTimestamp = Date.now()
	requestAnimationFrame(() => {
		if (!listRef) return
		scrollToBottom()
		const newDurationOffset = Date.now() - startTimestamp + durrationOffset
		if (newDurationOffset < duration) {
			lockScrollToBottom(duration, newDurationOffset)
		}
	})
}

const scrollListItemIntoView = (messageId: string) => {
	const listItemRef = listItemsMap[messageId]
	if (!listItemRef) return
	scrollIntoView(listItemRef)
}

const scrollListItemIntoViewWithAnimation = async (messageId: string) => {
	await tick()
	const listItemRef = listItemsMap[messageId]
	if (!listItemRef) return

	setTimeout(() => {
		scrollIntoView(listItemRef, { behavior: 'smooth' }, { duration: 500 })
	}, 100)
}

const scrollToTypingMessage = () => {
	if (!typingRef) return
	scrollIntoView(typingRef, { behavior: 'smooth' }, { duration: 500 })
}
// Rating
$effect(() => {
	if (
		$suggestedRating === RatingTarget.Ai &&
		$messageGroups &&
		$lastMessageId !== RATE_MESSAGE_ID &&
		!typingRef &&
		$messengerInputValue.length === 0
	)
		ratingTimeout.start()
})
$effect(() => {
	if (typingRef || $messengerInputValue.length !== 0 || !$suggestedRating) ratingTimeout.stop()
})
$effect(() => {
	if ($suggestedRating === RatingTarget.Agent) void addRatingMessage(RatingTarget.Agent)
})
$effect(() => {
	if (!$suggestedRating) deleteRatingMessage()
})

$effect(() => {
	if ($isAgentTyping && typingRef) scrollToTypingMessage()
})

$effect(() => {
	if ($lastMessageId && !$isReplyProcessing) {
		void scrollListItemIntoViewWithAnimation($lastMessageId)
	}
})
$effect(() => {
	$textareaHeight && scrollToBottom()
})
$effect(() => {
	if ($scrollContentListCounter > 0) scrollToBottom()
})

let messengerWasExpanded = $state($isMessengerFrameExpanded)
$effect(() => {
	// scroll down when frame is collapsing
	if (messengerWasExpanded) {
		lockScrollToBottom(250)
	}
	messengerWasExpanded = $isMessengerFrameExpanded
})
</script>

<div bind:this={listRef} class="p-4 flex-grow overflow-x-hidden overflow-y-scroll overscroll-contain">
	{#each groups as { id, messagesContext, align, agentId, showAvatar, useFullWidth, isBot } (id)}
		<MessageGroup {align} {agentId} {showAvatar} {isBot} {messagesContext}>
			{#each messagesContext as messageContext, index (messageContext.message.id)}
				{#key messageContext.message.content.text}
					<div
						bind:this={listItemsMap[messageContext.message.id]}
						class={[index !== 0 && 'mt-0.5', useFullWidth ? 'w-full' : 'max-w-[85%]', 'scroll-mt-2']}
					>
						<ContentListItem context={messagesContext[index]} />
					</div>
				{/key}
			{/each}
		</MessageGroup>
	{/each}
	{#if $isAgentTyping}
		<TypingMessage bind:ref={typingRef} />
	{/if}
</div>
